import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface OopsPageProps {
  /** Optional trace id to surface for support (from a route-boundary error). */
  traceId?: string;
}

/**
 * Last-resort route-boundary error page (`/oops`). Neutral-branded and public
 * so it renders even when the shell itself failed. Shows a monospace `traceId`
 * for support and offers "Try again" / "Return home".
 *
 * <p>
 * <b>Both actions reload the document (BUG-047).</b> They used to be
 * `<RouterLink to="/">`, which is inert when the page is already at `/` — and it
 * always is, because an identity restart lands there and the boundary renders in
 * place rather than navigating. Clicking either button did nothing at all: no
 * navigation, no request, no console output, and no way out of the screen short
 * of clearing site data.
 * </p>
 *
 * <p>
 * A router link is also the wrong tool here on principle. This page renders
 * because the app failed to bootstrap, so the thing worth retrying is the
 * bootstrap — which only a document load repeats. `reload()` retries the current
 * URL; "Return home" replaces it with `/` so a bad route is not retried forever.
 * </p>
 */
export function OopsPage({ traceId }: OopsPageProps) {
  const { t } = useTranslation();
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
      <Stack spacing={2} sx={{ maxWidth: 480, alignItems: 'flex-start' }}>
        <Typography variant="h4" component="h1">
          {t('oops.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t('oops.body')}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {t('oops.traceId', { id: traceId ?? '—' })}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => window.location.reload()} variant="contained">
            {t('actions.tryAgain')}
          </Button>
          <Button onClick={() => window.location.replace('/')} variant="outlined">
            {t('actions.returnHome')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
